import { connectionAtom, networkAtom, refreshNFTVoucherListAtom, umiAtom } from "@/atoms/settings"
import { Constants } from "@/programs/voucher_artifacts/constants.nft"
import VoucherNftIDL from "@/programs/voucher_artifacts/nft_voucher.json"
import { SolanaVoucherNft as VoucherNftType } from "@/programs/voucher_artifacts/voucher_nft.solana"
import { RENEC_NFT_VOUCHER_PROGRAM_ID, SOLANA_NFT_VOUCHER_PROGRAM_ID } from "@/utils/constants"
import { sliceString } from "@/utils/helpers"
import { FetchNftDetailResponseType, fetchNFTDetailSolana, fetchNftAssetsByCollectionsSolana } from "@/utils/nft"
import { DigitalAssetWithToken } from "@metaplex-foundation/mpl-token-metadata"
import { Umi } from "@metaplex-foundation/umi"
import * as anchor from "@project-serum/anchor"
import NodeWallet from "@project-serum/anchor/dist/cjs/nodewallet"
import { FetchNFT } from "@renec-foundation/nft-sdk"
import { BlockchainNetwork, ConstantsHelper } from "@renec-foundation/relend-adapter"
import { AccountInfo, Connection, Keypair, PublicKey } from "@solana/web3.js"
import axios from "axios"
import BigNumber from "bignumber.js"
import BN from "bn.js"
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import { useAtomValue } from "jotai"
import { useMemo } from "react"
import { useQuery } from "react-query"
dayjs.extend(customParseFormat)

export type NFTDetail = {
  attributes: { trait_type: string; value: string }[]
  name: string
  description: string
  symbol: string
  isValid: boolean
  isValidStartDate: boolean
  isValidEndDate: boolean
  isValidToken: boolean
  discountPercentage: BigNumber
  eligibleTokens: string
  maximumAmount: BigNumber
  voucherInfo: number
}

type MetadataData = {
  collection: { key: string }
  data: {
    name: string
    uri: string
  }
  mint: string
}

export type NFTDetailType = {
  campaignId?: number
  metadata?: MetadataData
  imageUrl?: string
  name: string
  mintAddress: string
  displayMintAddress: string
  detail: NFTDetail | null
}

export type ProgramNftDetail = {
  voucherInfo?: number
  voucherType?: number
  authority: PublicKey
  discountPercentage: number
  eligibleTokens: Array<PublicKey>
  endTime: BN
  maximumAmount: BN
  nftMint: PublicKey
  reserve: Array<BN>
  startTime: BN
}

export type SolanaProgramNftDetail = DigitalAssetWithToken & ProgramNftDetail

const RANDOM_KEYPAIR = Keypair.generate()
const RANDOM_WALLET = new NodeWallet(RANDOM_KEYPAIR)

const DATETIME_FORMAT = "DD/MM/YYYY HH:mm:ss"

const fetchLendingNFTs = async (network: BlockchainNetwork, connection: Connection, wallet: PublicKey | null) => {
  if (!wallet) return []

  try {
    const NFTModules = new FetchNFT(connection, wallet)
    const nfts = await NFTModules.fetchNFTList()
    const lendingCollectionKeys = ConstantsHelper.getNFTCollectionKeys(network)

    const data = nfts
      .filter(
        (nft) => nft && nft.json && nft.metadata && lendingCollectionKeys.includes(nft.metadata.collection?.key || ""),
      )
      .map((nft) => ({
        metadata: nft?.metadata,
        imageUrl: nft?.json?.image,
        name: nft?.json?.name,
        mintAddress: nft?.metadata.mint,
        displayMintAddress: sliceString(nft?.metadata.mint || "", 4, 4),
      }))

    return data as NFTDetailType[]
  } catch {
    return []
  }
}

/**
 * Parse the NFT detail from the program with the imageURL retrieved from metadata
 */
const parseProgramNftDetailSolana = (repayTokenAddress: string, nftDetail: SolanaProgramNftDetail) => {
  const currentDate = dayjs()
  const parsedNftDetail: NFTDetail = {
    attributes: [],
    name: nftDetail.metadata.name,
    symbol: nftDetail.metadata.symbol,
    description: "",
    isValid: false,
    isValidStartDate: false,
    isValidEndDate: false,
    isValidToken: false,
    eligibleTokens: "",
    discountPercentage: new BigNumber(nftDetail.discountPercentage),
    maximumAmount: new BigNumber(nftDetail.maximumAmount.toString()),
    voucherInfo: 0,
  }
  const startTime = nftDetail.startTime.toNumber() * 1000
  const parsedStartTime = dayjs(startTime, undefined, true)
  parsedNftDetail.isValidStartDate = parsedStartTime.isBefore(currentDate)

  const endTime = nftDetail.endTime.toNumber() * 1000
  const parsedEndTime = dayjs(endTime, undefined, true)
  parsedNftDetail.isValidEndDate = parsedEndTime.isAfter(currentDate)

  const eligibleTokens = nftDetail.eligibleTokens.map((token) => token.toBase58()).join(",")
  parsedNftDetail.isValidToken = eligibleTokens === "" || eligibleTokens.split(",").includes(repayTokenAddress)
  parsedNftDetail.eligibleTokens = eligibleTokens

  const discountPercentage = nftDetail.discountPercentage
  parsedNftDetail.discountPercentage = new BigNumber(discountPercentage.toString())

  const maximumAmount = nftDetail.maximumAmount
  parsedNftDetail.maximumAmount = new BigNumber(maximumAmount.toString())

  const voucherInfo = nftDetail.voucherInfo
  const voucherType = nftDetail.voucherType

  if (voucherInfo) {
    parsedNftDetail.voucherInfo = Number(voucherInfo)
  } else if (voucherType) {
    parsedNftDetail.voucherInfo = Number(voucherType)
  }

  parsedNftDetail.isValid =
    parsedNftDetail.isValidStartDate && parsedNftDetail.isValidEndDate && parsedNftDetail.isValidToken
  return parsedNftDetail
}

/**
 * Parse the NFT detail purely from the metadata url
 */
const parseNFTDetailRenec = async (repayTokenAddress: string, nft?: NFTDetailType) => {
  if (!nft) return

  const url = nft.metadata?.data.uri
  if (!url) return

  try {
    const { data } = await axios.get(url)
    const nftDetail = data as NFTDetail
    const attributes = nftDetail.attributes

    const currentDate = dayjs()
    const start_time = attributes.find((x) => x.trait_type === "start_time")
    if (start_time) {
      const parsedStartTime = dayjs(start_time.value, DATETIME_FORMAT, true)
      nftDetail.isValidStartDate = parsedStartTime.isBefore(currentDate)
    }

    const end_time = attributes.find((x) => x.trait_type === "end_time")
    if (end_time) {
      const parsedEndTime = dayjs(end_time.value, DATETIME_FORMAT, true)
      nftDetail.isValidEndDate = parsedEndTime.isAfter(currentDate)
    }

    const eligible_tokens = attributes.find((x) => x.trait_type === "eligible_tokens")
    if (eligible_tokens) {
      nftDetail.isValidToken =
        eligible_tokens.value === "" || eligible_tokens.value.split(",").includes(repayTokenAddress)
      nftDetail.eligibleTokens = eligible_tokens.value
    }

    const discount_percentage = attributes.find((x) => x.trait_type === "discount_percentage")
    if (discount_percentage) {
      nftDetail.discountPercentage = new BigNumber(discount_percentage.value)
    }

    const maximum_amount = attributes.find((x) => x.trait_type === "maximum_amount")
    if (maximum_amount) {
      nftDetail.maximumAmount = new BigNumber(maximum_amount.value)
    }

    const voucher_info = attributes.find((x) => x.trait_type === "voucher_info")
    const voucher_type = attributes.find((x) => x.trait_type === "voucher_type")
    if (voucher_info) {
      nftDetail.voucherInfo = Number(voucher_info.value)
    } else if (voucher_type) {
      nftDetail.voucherInfo = Number(voucher_type.value)
    }

    nftDetail.isValid = nftDetail.isValidStartDate && nftDetail.isValidEndDate && nftDetail.isValidToken
    return nftDetail
  } catch {
    return
  }
}

type NftDiscount = {
  voucherInfo?: number
  voucherType?: number
  discountPercentage: number
  maximumAmount: BN
  startTime: BN
  endTime: BN
  nftMint: PublicKey
  authority: PublicKey
  reserve: BN[]
  eligibleTokens: PublicKey[]
}

type InnerPromiseResult = NftDiscount | FetchNftDetailResponseType | number

type AggregatedNftData = DigitalAssetWithToken &
  NftDiscount & {
    image: string | null
  }

/**
 * Checks if a voucher account exists for a given NFT mint address
 * @param connection Connection
 * @param programId The program ID (SOLANA_NFT_VOUCHER_PROGRAM_ID or RENEC_NFT_VOUCHER_PROGRAM_ID)
 * @param nftMint The mint address of the NFT
 * @param voucherType 'borrowFee' or 'repayDebt'
 * @returns A boolean indicating whether the account exists
 */
export const checkVoucherAccountExists = async (
  connection: Connection,
  programId: string,
  nftMint: PublicKey,
  voucherType: "borrowFee" | "repayDebt",
): Promise<boolean> => {
  const seed = voucherType === "borrowFee" ? Constants.BORROW_FEE_VOUCHER : Constants.REPAY_DEBT_VOUCHER_SEED
  const [voucherAddress] = PublicKey.findProgramAddressSync(
    [Buffer.from(seed), nftMint.toBuffer()],
    new PublicKey(programId),
  )

  try {
    const accountInfo: AccountInfo<Buffer> | null = await connection.getAccountInfo(voucherAddress)
    return accountInfo !== null
  } catch (error) {
    console.error("Error checking voucher account:", error)
    return false
  }
}

const REPAY_DEBT_VOUCHER_INFO = 0
const BORROW_FEE_VOUCHER_INFO = 1
const PROGRAM_DATA_INDEX = 0
const NFT_METADATA_DETAIL_INDEX = 1
const VOUCHER_INFO_INDEX = 2

export const fetchNFTSolana = async (
  wallet: PublicKey | null,
  repayTokenAddress: string,
  network: BlockchainNetwork,
  umi: Umi,
  program: anchor.Program<VoucherNftType>,
) => {
  try {
    const lendingCollectionKeys = ConstantsHelper.getNFTCollectionKeys(network)
    if (wallet) {
      const assetsByCollection = await fetchNftAssetsByCollectionsSolana(umi, wallet, lendingCollectionKeys)
      const promises = []
      for (const relatedAsset of assetsByCollection) {
        const nftMint = new PublicKey(relatedAsset.publicKey)
        const repayDebtVoucherExists = await checkVoucherAccountExists(
          program.provider.connection,
          SOLANA_NFT_VOUCHER_PROGRAM_ID,
          nftMint,
          "repayDebt",
        )
        const borrowFeeVoucherExists = await checkVoucherAccountExists(
          program.provider.connection,
          SOLANA_NFT_VOUCHER_PROGRAM_ID,
          nftMint,
          "borrowFee",
        )

        try {
          const fetchNftDetailRequest = fetchNFTDetailSolana(relatedAsset.metadata)
          const innerPromises: Promise<InnerPromiseResult | number | null>[] = [fetchNftDetailRequest]

          if (repayDebtVoucherExists) {
            const [repayDebtVoucher] = PublicKey.findProgramAddressSync(
              [Buffer.from(Constants.REPAY_DEBT_VOUCHER_SEED), nftMint.toBuffer()],
              new PublicKey(SOLANA_NFT_VOUCHER_PROGRAM_ID),
            )
            innerPromises.unshift(program.account.repayDebtVoucher.fetch(repayDebtVoucher))
            innerPromises.push(Promise.resolve(REPAY_DEBT_VOUCHER_INFO))
          }

          if (borrowFeeVoucherExists) {
            const [borrowFeeVoucher] = PublicKey.findProgramAddressSync(
              [Buffer.from(Constants.BORROW_FEE_VOUCHER), nftMint.toBuffer()],
              new PublicKey(SOLANA_NFT_VOUCHER_PROGRAM_ID),
            )
            innerPromises.unshift(program.account.borrowFeeVoucher.fetch(borrowFeeVoucher))
            innerPromises.push(Promise.resolve(BORROW_FEE_VOUCHER_INFO))
          }
          promises.push(Promise.all(innerPromises))
        } catch (error) {
          console.error(error)
        }
      }
      const nftDatasWithDetail = (await Promise.all(promises)) as [NftDiscount, FetchNftDetailResponseType, number][]

      const assetsByCollectionWithDetail = assetsByCollection
        .filter(Boolean)
        .map((item) => {
          const assetData = nftDatasWithDetail.find((nftWithDetail) => {
            const nftData = nftWithDetail[PROGRAM_DATA_INDEX] as NftDiscount
            return nftData.nftMint.toBase58() === item.publicKey
          })

          if (!assetData) {
            return null
          }

          const nftData = assetData[PROGRAM_DATA_INDEX]
          const image =
            assetData && assetData[NFT_METADATA_DETAIL_INDEX] && assetData[NFT_METADATA_DETAIL_INDEX].json
              ? assetData[NFT_METADATA_DETAIL_INDEX].json.image
              : null
          return {
            ...item,
            ...nftData,
            voucherInfo: assetData[VOUCHER_INFO_INDEX]!,
            image,
          }
        })
        .filter(Boolean) as AggregatedNftData[]

      const nftDetails = assetsByCollectionWithDetail.map((nftDetail) => {
        return {
          name: nftDetail.metadata.name,
          symbol: nftDetail.metadata.symbol,
          mintAddress: nftDetail.publicKey,
          displayMintAddress: sliceString(nftDetail.publicKey, 4, 4),
          imageUrl: nftDetail.image,
          detail: parseProgramNftDetailSolana(repayTokenAddress, nftDetail),
        } as NFTDetailType
      })
      return nftDetails
    }
  } catch (err) {
    console.error(err)
    return []
  }
}

export const fetchNFTRenec = async (
  wallet: PublicKey | null,
  connection: Connection,
  repayTokenAddress: string,
  network: BlockchainNetwork,
) => {
  try {
    const ans = await fetchLendingNFTs(network, connection, wallet)
    const promises = ans.map(async (item, index) => {
      const value = await parseNFTDetailRenec(repayTokenAddress, item)
      ans[index].detail = value ? value : null
    })
    await Promise.all(promises)

    return ans
  } catch (error) {
    console.error(error)
    return []
  }
}

export const useFetchNftWallet = (repayTokenAddress: string, wallet: PublicKey | null) => {
  const umi = useAtomValue(umiAtom)
  const network = useAtomValue(networkAtom)
  const connection = useAtomValue(connectionAtom)
  const isRenecNetwork = useMemo(() => network === BlockchainNetwork.RENEC, [network])
  const shouldRefreshNFTVoucherList = useAtomValue(refreshNFTVoucherListAtom)

  const provider = useMemo(
    () =>
      new anchor.AnchorProvider(connection, RANDOM_WALLET, {
        commitment: "confirmed",
      }),
    [connection],
  )
  const program = useMemo(
    () =>
      isRenecNetwork
        ? (new anchor.Program(
            VoucherNftIDL as anchor.Idl,
            new PublicKey(RENEC_NFT_VOUCHER_PROGRAM_ID),
            provider,
          ) as any as anchor.Program<VoucherNftType>)
        : (new anchor.Program(
            VoucherNftIDL as anchor.Idl,
            new PublicKey(SOLANA_NFT_VOUCHER_PROGRAM_ID),
            provider,
          ) as any as anchor.Program<VoucherNftType>),
    [isRenecNetwork, provider],
  )

  return useQuery(
    [
      `fetchNftAddress-${wallet?.toBase58()}-${network}-${shouldRefreshNFTVoucherList}`,
      wallet?.toBase58(),
      network,
      shouldRefreshNFTVoucherList,
    ],
    async () => {
      return isRenecNetwork
        ? await fetchNFTRenec(wallet, connection, repayTokenAddress, network)
        : await fetchNFTSolana(wallet, repayTokenAddress, network, umi, program)
    },
    {
      staleTime: 60000,
      enabled: !!wallet,
    },
  )
}

export const useFetchNFTWalletByCollection = (wallet: PublicKey, collection: string) => {
  const connection = useAtomValue(connectionAtom)
  const network = useAtomValue(networkAtom)

  const ans = useQuery(
    [`fetchNftAddress-${wallet ? wallet.toBase58() : ""}-${collection}`],
    async () => {
      const ans = await fetchLendingNFTs(network, connection, wallet)
      return ans
    },
    { staleTime: 60000 },
  )

  if (!ans) return { data: [] }

  const { data } = ans
  if (!data) return { data: [] }

  if (collection.toUpperCase() === "ALL") return { data }

  const res = data.filter((item) => item?.metadata?.collection?.key === collection)

  return {
    data: res,
    refetch: ans.refetch,
  }
}
